import React, { useState } from 'react';
import { 
  Shield, Globe, Battery, Activity, CheckCircle, 
  Truck, CreditCard, Package, MapPin, Wifi,
  AlertCircle, ChevronRight, Clock, Zap, Star
} from 'lucide-react';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { supabase } from '../lib/supabase';

const PurchaseLanding: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useSupabaseAuth();

  const handlePurchase = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Call Edge Function to create Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId: process.env.REACT_APP_STRIPE_PRICE_ID, // Set this in your .env
          successUrl: `${window.location.origin}/purchase-success`,
          cancelUrl: `${window.location.origin}/purchase`,
          customerEmail: user?.email,
          metadata: {
            userId: user?.id
          }
        }
      });

      if (error) throw error;
      
      if (!data || !data.url) {
        throw new Error('Failed to get checkout URL');
      }

      // Redirect to Stripe Checkout using the URL directly
      window.location.href = data.url;
    } catch (err: any) {
      console.error('Purchase error:', err);
      setError(err.message || 'Failed to start checkout process');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-traqpack-yellow/20 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-traqpack-yellow/20 text-traqpack-yellow px-4 py-2 rounded-full mb-6 backdrop-blur-sm">
                <Zap className="w-4 h-4" />
                <span className="text-sm font-semibold">Limited Time Offer</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-black mb-6 leading-tight">
                Track Everything.<br />
                <span className="text-traqpack-yellow">Worry Never.</span>
              </h1>
              
              <p className="text-xl mb-8 text-gray-300 leading-relaxed">
                Professional GPS tracking for your valuable assets. 
                Up to 1 year battery life, global coverage, and AI-powered insights.
              </p>

              {/* Price Badge */}
              <div className="bg-traqpack-yellow text-black inline-block px-6 py-4 rounded-2xl mb-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black">$60</span>
                  <span className="text-lg font-semibold">one-time</span>
                </div>
                <p className="text-sm mt-1 font-medium">+ 1 Year Free Subscription Included</p>
              </div>

              {/* Key Features */}
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-traqpack-yellow flex-shrink-0" />
                  <span className="text-gray-300">Up to 1 year battery life - set it and forget it</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-traqpack-yellow flex-shrink-0" />
                  <span className="text-gray-300">Global coverage in 150+ countries</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-traqpack-yellow flex-shrink-0" />
                  <span className="text-gray-300">AI-powered movement detection & alerts</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-traqpack-yellow flex-shrink-0" />
                  <span className="text-gray-300">Free shipping & 30-day money-back guarantee</span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handlePurchase}
                disabled={isLoading}
                className="bg-traqpack-yellow text-black font-bold px-8 py-4 rounded-xl text-lg hover:bg-yellow-400 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-3 shadow-xl"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    <span>Buy Now - Free Shipping</span>
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Trust Badges */}
              <div className="flex items-center gap-6 mt-8">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-400">Secure Checkout</span>
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-400">Free Shipping</span>
                </div>
              </div>
            </div>

            {/* Product Image Placeholder */}
            <div className="relative">
              <div className="bg-gradient-to-br from-traqpack-yellow/30 to-traqpack-yellow/10 rounded-3xl p-8 backdrop-blur-sm">
                <div className="bg-black/50 rounded-2xl p-12 flex items-center justify-center">
                  <div className="text-center">
                    <Package className="w-32 h-32 text-traqpack-yellow mx-auto mb-4" />
                    <p className="text-white text-lg font-semibold">TraqPack T1</p>
                    <p className="text-gray-400 text-sm mt-2">Actual size: 2.5" × 1.5" × 0.5"</p>
                  </div>
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-traqpack-yellow text-black px-4 py-2 rounded-full font-bold shadow-lg">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-current" />
                  <span>4.9/5</span>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white text-black px-4 py-2 rounded-full font-semibold shadow-lg">
                <div className="flex items-center gap-2">
                  <Battery className="w-4 h-4" />
                  <span>1 Year Battery</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-black text-gray-900 mb-4">
            Everything You Need to <span className="text-traqpack-yellow">Track Smart</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Professional-grade tracking technology designed for businesses and individuals who need reliability
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center mb-6">
              <Globe className="w-7 h-7 text-black" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Global Coverage</h3>
            <p className="text-gray-600">
              Works in 150+ countries with automatic network switching for uninterrupted tracking anywhere
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center mb-6">
              <Battery className="w-7 h-7 text-black" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Long-Lasting Battery</h3>
            <p className="text-gray-600">
              Up to 1 year battery life with efficient power management - minimal maintenance required
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center mb-6">
              <Activity className="w-7 h-7 text-black" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Smart AI Alerts</h3>
            <p className="text-gray-600">
              Get intelligent notifications for unusual movement patterns and predictive maintenance alerts
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center mb-6">
              <MapPin className="w-7 h-7 text-black" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Geofencing</h3>
            <p className="text-gray-600">
              Create virtual boundaries and receive instant alerts when assets enter or leave designated areas
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center mb-6">
              <Wifi className="w-7 h-7 text-black" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Real-Time Updates</h3>
            <p className="text-gray-600">
              Track your assets in real-time with updates every 60 seconds when in motion
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="w-14 h-14 bg-traqpack-yellow/20 rounded-xl flex items-center justify-center mb-6">
              <Shield className="w-7 h-7 text-black" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Bank-Level Security</h3>
            <p className="text-gray-600">
              End-to-end encryption and secure data storage keep your location data private and protected
            </p>
          </div>
        </div>
      </div>

      {/* What's Included Section */}
      <div className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4">What's Included</h2>
            <p className="text-xl text-gray-400">Everything you need to start tracking immediately</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-6 text-traqpack-yellow">Device Package</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>TraqPack T1 GPS Tracker Device</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Pre-activated SIM card (global coverage)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Magnetic mounting case</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Quick start guide</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-6 text-traqpack-yellow">1-Year Subscription</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>100 AI-powered queries per month</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>50 smart notifications per month</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Unlimited location history (365 days)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>Web & mobile app access</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Renewal Info */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-traqpack-yellow/20 text-traqpack-yellow px-6 py-3 rounded-full">
              <Clock className="w-5 h-5 inline-block mr-2" />
              <span className="font-semibold">After Year 1: Only $10/year to continue service</span>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="bg-traqpack-yellow py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black text-black mb-6">
            Ready to Never Lose Track Again?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
            Join thousands of businesses and individuals who trust TraqPack for their valuable assets
          </p>

          <button
            onClick={handlePurchase}
            disabled={isLoading}
            className="bg-black text-traqpack-yellow font-bold px-10 py-5 rounded-xl text-xl hover:bg-gray-900 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none inline-flex items-center gap-3 shadow-2xl"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-traqpack-yellow"></div>
                <span>Processing...</span>
              </>
            ) : (
              <>
                <Package className="w-6 h-6" />
                <span>Get Your TraqPack Now</span>
                <ChevronRight className="w-6 h-6" />
              </>
            )}
          </button>

          <div className="mt-8 flex items-center justify-center gap-8 text-black/60">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">30-Day Money Back</span>
            </div>
            <div className="flex items-center gap-2">
              <Truck className="w-5 h-5" />
              <span className="text-sm font-medium">Free US Shipping</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              <span className="text-sm font-medium">Secure Payment</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseLanding;